import React from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { useAppSelector } from '@core/redux/hooks';
import PrecisionService from '@core/services/PrecisionService';
import { getLineCategory, hasMedical } from '@utils/helper-utils';

import { FormValues } from '../types';

import { useFormikContext } from 'formik';
import { LineCategory } from 'src/enums';

type Props = {
  status: string;
  currencySymbol: string;
  lineId: string;
  lineCode: string;
};

const EndorsementCostChargesWidgetAtom: React.FC<Props> = ({
  status,
  currencySymbol,
  lineId,
  lineCode
}) => {
  const user = useAppSelector((state) => state.user);
  const { values, handleBlur, setFieldValue, touched, errors } =
    useFormikContext<FormValues>();

  const s = String(status || '').toUpperCase();
  const isLocked = s === 'CONFIRMED' || s === 'CANCELLED';
  const canEditLine = user?.info?.editableChargesLines?.find?.(
    (i: any) => i?.lineID === lineId
  );

  const disabledRow = isLocked || !canEditLine;
  const c = values?.chargesAndCost ?? {};
  const amendmentType = values?.amendmentDetails?.amendmentType || '';
  const isCancellation = amendmentType.toLowerCase().includes('cancellation');
  const isExpat = getLineCategory(lineCode) === LineCategory.Expat;

  return (
    <WidgetPaper>
      <WidgetSection title="Cost & Charges" isFieldsGrid fieldsPerRow={1}>
        {isCancellation ? (
          // Cancellation type fields
          <>
            {/* Basic Premium fields - only show for line code 43 */}
            {isExpat && (
              <WidgetSection isFieldsGrid fieldsPerRow={2}>
                <CurrencyFormField
                  key="chargesAndCost.basicPremium"
                  name="chargesAndCost.basicPremium"
                  title="Basic Premium*"
                  placeholder="Basic Premium"
                  value={c?.basicPremium}
                  onBlur={(e) => handleBlur(e)}
                  onChange={() => {}}
                  error={
                    (touched?.chargesAndCost as any)?.basicPremium
                      ? (errors?.chargesAndCost as any)?.basicPremium
                      : ''
                  }
                  disabled
                  material
                  currencySymbol={currencySymbol}
                />
                <CurrencyFormField
                  key="chargesAndCost.basicPremiumVariance"
                  name="chargesAndCost.basicPremiumVariance"
                  title="Basic Premium Variance*"
                  placeholder="Basic Premium Variance"
                  value={c?.basicPremiumVariance}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) =>
                    setFieldValue(
                      'chargesAndCost.basicPremiumVariance',
                      e.target.value
                    )
                  }
                  error={
                    (touched?.chargesAndCost as any)?.basicPremiumVariance
                      ? (errors?.chargesAndCost as any)?.basicPremiumVariance
                      : ''
                  }
                  disabled={disabledRow}
                  material
                  maxValue={0}
                  currencySymbol={currencySymbol}
                />
              </WidgetSection>
            )}

            {/* Charges Amount fields */}
            <WidgetSection isFieldsGrid fieldsPerRow={2}>
              <CurrencyFormField
                key="chargesAndCost.chargesAmount"
                name="chargesAndCost.chargesAmount"
                title="Charges Amount*"
                placeholder="Charges Amount"
                value={c?.chargesAmount}
                onBlur={(e) => handleBlur(e)}
                onChange={() => {}}
                error={
                  (touched?.chargesAndCost as any)?.chargesAmount
                    ? (errors?.chargesAndCost as any)?.chargesAmount
                    : ''
                }
                disabled
                material
                maxValue={0}
                currencySymbol={currencySymbol}
              />
              <CurrencyFormField
                key="chargesAndCost.chargesAmountVariance"
                name="chargesAndCost.chargesAmountVariance"
                title="Charges Amount Variance*"
                placeholder="Charges Amount Variance"
                value={c?.chargesAmountVariance}
                onBlur={(e) => handleBlur(e)}
                onChange={(e) =>
                  setFieldValue(
                    'chargesAndCost.chargesAmountVariance',
                    e.target.value
                  )
                }
                error={
                  (touched?.chargesAndCost as any)?.chargesAmountVariance
                    ? (errors?.chargesAndCost as any)?.chargesAmountVariance
                    : ''
                }
                disabled={disabledRow}
                material
                maxValue={0}
                currencySymbol={currencySymbol}
              />
            </WidgetSection>

            {/* TPA Fees fields - hide for medical/travel lines */}

            <WidgetSection isFieldsGrid fieldsPerRow={2}>
              <CurrencyFormField
                key="chargesAndCost.tpaFees"
                name="chargesAndCost.tpaFees"
                title="TPA Fees*"
                placeholder="TPA Fees"
                value={c?.tpaFees}
                error={
                  (touched?.chargesAndCost as any)?.tpaFees
                    ? (errors?.chargesAndCost as any)?.tpaFees
                    : ''
                }
                onBlur={(e) => handleBlur(e)}
                onChange={() => {}}
                disabled
                material
                currencySymbol={currencySymbol}
              />
              <CurrencyFormField
                key="chargesAndCost.tpaFeesVariance"
                name="chargesAndCost.tpaFeesVariance"
                title="TPA Fees Variance*"
                placeholder="TPA Fees Variance"
                value={c?.tpaFeesVariance}
                error={
                  (touched?.chargesAndCost as any)?.tpaFeesVariance
                    ? (errors?.chargesAndCost as any)?.tpaFeesVariance
                    : ''
                }
                onBlur={(e) => handleBlur(e)}
                onChange={(e) =>
                  setFieldValue(
                    'chargesAndCost.tpaFeesVariance',
                    e.target.value
                  )
                }
                disabled={disabledRow}
                material
                maxValue={0}
                currencySymbol={currencySymbol}
              />
            </WidgetSection>

            {/* Cancellation Cost */}
            <WidgetSection isFieldsGrid fieldsPerRow={1}>
              <CurrencyFormField
                key="chargesAndCost.cancellationCost"
                name="chargesAndCost.cancellationCost"
                title="Cancellation Cost*"
                placeholder="Cancellation Cost"
                value={c?.cancellationCost}
                onBlur={(e) => handleBlur(e)}
                onChange={(e) =>
                  setFieldValue(
                    'chargesAndCost.cancellationCost',
                    e.target.value
                  )
                }
                error={
                  (touched?.chargesAndCost as any)?.cancellationCost
                    ? (errors?.chargesAndCost as any)?.cancellationCost
                    : ''
                }
                disabled={disabledRow}
                material
                minValue={0}
                currencySymbol={currencySymbol}
              />
            </WidgetSection>
          </>
        ) : (
          // Endorsement type fields (original layout)
          <>
            <CurrencyFormField
              key="chargesAndCost.amendmentCost"
              name="chargesAndCost.amendmentCost"
              title="Amendment Cost*"
              placeholder="Amendment Cost"
              value={c?.amendmentCost}
              onBlur={(e) => handleBlur(e)}
              onChange={(e) =>
                setFieldValue('chargesAndCost.amendmentCost', e.target.value)
              }
              error={
                (touched?.chargesAndCost as any)?.amendmentCost
                  ? (errors?.chargesAndCost as any)?.amendmentCost
                  : ''
              }
              disabled={disabledRow}
              material
              currencySymbol={currencySymbol}
            />
          </>
        )}
      </WidgetSection>

      {!isCancellation && (
        <WidgetSection isFieldsGrid fieldsPerRow={2}>
          <PercentageFormField
            key="chargesAndCost.chargesPercentage"
            name="chargesAndCost.chargesPercentage"
            title="Charges Percentage*"
            placeholder="Charges Percentage"
            value={PrecisionService.multiplyBy100(c?.chargesPercentage || 0)}
            error={
              (touched?.chargesAndCost as any)?.chargesPercentage
                ? (errors?.chargesAndCost as any)?.chargesPercentage
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={() => {}}
            disabled
            material
          />
          <PercentageFormField
            key="newChargesPercentage"
            name="chargesAndCost.newChargesPercentage"
            title="New Charges Percentage*"
            placeholder="New Charges Percentage"
            value={PrecisionService.multiplyBy100(c?.newChargesPercentage || 0)}
            onBlur={(e) => handleBlur(e)}
            error={
              (touched?.chargesAndCost as any)?.newChargesPercentage
                ? (errors?.chargesAndCost as any)?.newChargesPercentage
                : ''
            }
            maxDecimalPrecision={3}
            onChange={(e) =>
              setFieldValue(
                'chargesAndCost.newChargesPercentage',
                PrecisionService.divideBy100(Number(e.target.value) || 0)
              )
            }
            disabled={disabledRow}
            material
          />
        </WidgetSection>
      )}

      {!isCancellation && !hasMedical(lineCode) && (
        <WidgetSection isFieldsGrid fieldsPerRow={2}>
          <CurrencyFormField
            key="chargesAndCost.tpaFees"
            name="chargesAndCost.tpaFees"
            title="TPA Fees*"
            placeholder="TPA Fees"
            value={c?.tpaFees}
            error={
              (touched?.chargesAndCost as any)?.tpaFees
                ? (errors?.chargesAndCost as any)?.tpaFees
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={() => {}}
            disabled
            material
            currencySymbol={currencySymbol}
          />
          <CurrencyFormField
            key="chargesAndCost.newTPAFees"
            name="chargesAndCost.newTPAFees"
            title="New TPA Fees*"
            placeholder="New TPA Fees"
            value={c?.newTPAFees}
            error={
              (touched?.chargesAndCost as any)?.newTPAFees
                ? (errors?.chargesAndCost as any)?.newTPAFees
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('chargesAndCost.newTPAFees', e.target.value)
            }
            disabled={disabledRow}
            material
            currencySymbol={currencySymbol}
          />
        </WidgetSection>
      )}
    </WidgetPaper>
  );
};

export { EndorsementCostChargesWidgetAtom };
